import React, { FC, MutableRefObject, useCallback, useEffect, useState } from 'react';
import { BlockWithNotes, StudyGuideBlockContent } from '../../../../../config/api/models/Block';
import replace from 'lodash/fp/replace';
import { WysiwygEditor } from '../../../../shared/components';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { ERROR_MESSAGE_DURATION, PatchStatus, SUCCESS_MESSAGE_DURATION } from '../../../constants';
import {
  StudyGuideConceptsColumn,
  StudyGuideMobileTitle,
  StudyGuideQuestionColumn,
  StudyGuideSummaryColumn,
  StudyGuideWrapper,
} from './atoms/Atoms';
import { BlockFeedbackProps } from '../../../../feedback';
import { useAppDispatch, useAppSelector } from '../../../../shared/hooks/useAppDispatch';
import { PatchStatusMessage } from './PatchStatusMessage';
import { patchBlock } from '../../../../shared/slices/blocks/api/blocks';
import FroalaEditor from 'react-froala-wysiwyg';

type StudyGuideBlockProps = BlockFeedbackProps & {
  block: BlockWithNotes;
  pageId: string;
  bookId: string;
  readOnly?: boolean;
  ref?: Element | undefined;
};

export const StudyGuideBlock: FC<StudyGuideBlockProps> = ({
  block,
  pageId,
  bookId,
  readOnly,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));
  const dispatch = useAppDispatch();
  const [patchStatus, setPatchStatus] = useState<PatchStatus>(PatchStatus.INITIAL);
  const [editorRefQuestion, setEditorRefQuestion] =
    useState<MutableRefObject<FroalaEditor | null>>();
  const [editorRefConcepts, setEditorRefConcepts] =
    useState<MutableRefObject<FroalaEditor | null>>();
  const [editorRefSummary, setEditorRefSummary] = useState<MutableRefObject<FroalaEditor | null>>();
  const loading = useAppSelector((state) => state.blocks.loading);
  const error = useAppSelector((state) => state.blocks.error);
  const success = useAppSelector((state) => state.blocks.success);
  const [isEditorReadOnly, setIsEditorReadOnly] = useState(readOnly);

  const requestPatchBlock = useCallback(() => {
    const payload: StudyGuideBlockContent = {
      question: replace('fr-video fr-deletable')('fr-video fr-active fr-deletable')(
        editorRefQuestion?.current?.getEditor().el.innerHTML
      ),
      concepts: replace('fr-video fr-deletable')('fr-video fr-active fr-deletable')(
        editorRefConcepts?.current?.getEditor().el.innerHTML
      ),
      summary: replace('fr-video fr-deletable')('fr-video fr-active fr-deletable')(
        editorRefSummary?.current?.getEditor().el.innerHTML
      ),
    };

    dispatch(
      patchBlock({
        bookId,
        pageId,
        blockId: block.id,
        payload: {
          content: payload,
        },
      })
    );
  }, [
    block.id,
    bookId,
    dispatch,
    pageId,
    editorRefConcepts,
    editorRefQuestion,
    editorRefSummary,
  ]);

  useEffect(() => {
    if (!isEditorReadOnly && readOnly) {
      requestPatchBlock();
    } else {
      setIsEditorReadOnly(readOnly);
    }

    return () => {};
  }, [readOnly, isEditorReadOnly, requestPatchBlock]);

  useEffect(() => {
    if (!loading && success && readOnly && !isEditorReadOnly) {
      setIsEditorReadOnly(readOnly);
    }
  }, [loading, success, readOnly, isEditorReadOnly]);

  useEffect(() => {
    if (loading) setPatchStatus(PatchStatus.INITIAL);
    if (success && !loading) setPatchStatus(PatchStatus.SUCCESS);
    if (error) setPatchStatus(PatchStatus.ERROR);
  }, [loading, success, error, setPatchStatus]);

  useEffect(() => {
    switch (patchStatus) {
      case PatchStatus.SUCCESS:
        setTimeout(() => setPatchStatus(PatchStatus.INITIAL), SUCCESS_MESSAGE_DURATION);
        break;
      case PatchStatus.ERROR:
        setTimeout(() => setPatchStatus(PatchStatus.INITIAL), ERROR_MESSAGE_DURATION);
        break;
    }
  }, [patchStatus]);

  return (
    <StudyGuideWrapper mobile={isMobile}>
      {!readOnly && (
        <PatchStatusMessage hoverActive={false} title={block.title} status={patchStatus} />
      )}
      <StudyGuideQuestionColumn mobile={isMobile}>
        {isMobile && (
          <StudyGuideMobileTitle>
            {t('trajectory.sections.study_guide.headers.questionMobile')}
          </StudyGuideMobileTitle>
        )}
        <WysiwygEditor
          id={`wysiwyg_${block.id}_question`}
          value={block.studyGuideContent?.question || ''}
          onLoadRef={setEditorRefQuestion}
          readOnly={isEditorReadOnly}
          type="studyGuideQuestion"
        />
      </StudyGuideQuestionColumn>
      <StudyGuideConceptsColumn mobile={isMobile}>
        {isMobile && (
          <StudyGuideMobileTitle>
            {t('trajectory.sections.study_guide.headers.concepts')}
          </StudyGuideMobileTitle>
        )}
        <WysiwygEditor
          id={`wysiwyg_${block.id}_concepts`}
          value={block.studyGuideContent?.concepts || ''}
          onLoadRef={setEditorRefConcepts}
          readOnly={isEditorReadOnly}
          type="studyGuideConcepts"
        />
      </StudyGuideConceptsColumn>
      <StudyGuideSummaryColumn mobile={isMobile}>
        {isMobile && (
          <StudyGuideMobileTitle>
            {t('trajectory.sections.study_guide.headers.summary')}
          </StudyGuideMobileTitle>
        )}
        <WysiwygEditor
          id={`wysiwyg_${block.id}_summary`}
          value={block.studyGuideContent?.summary || ''}
          onLoadRef={setEditorRefSummary}
          readOnly={isEditorReadOnly}
          type="normal"
        />
      </StudyGuideSummaryColumn>
    </StudyGuideWrapper>
  );
};
